import { UpdateUserListInput } from '#app/__generated__/api.js';
import { useUserListMutationsCreateUserListMutation } from '#app/__generated__/relay/useUserListMutationsCreateUserListMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';

export const useUserListMutations = () => {
  const toast = useToast();
  const { track } = useAnalytics();

  const [createList, isCreatingNewList] =
    useContraMutation<useUserListMutationsCreateUserListMutation>(graphql`
      mutation useUserListMutationsCreateUserListMutation(
        $input: CreateUserListInput!
      ) {
        createUserList(input: $input) {
          errors {
            __typename
            message
          }
          userList {
            id
          }
          userAccount {
            ...NetworkTableV2_userLists_userAccount
            ...AddToUserListsModal_userAccount
          }
        }
      }
    `);

  const createUserList = useCallback(
    ({
      name,
      onSuccess,
    }: {
      name: string;
      onSuccess?: (newUserListId: string) => void;
    }) => {
      track('button', {
        action: 'tapped',
        button_name: 'network_create_user_list',
      });
      createList({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;

          if (payloadErrors) {
            for (const payloadError of payloadErrors) {
              const eventId = captureException(payloadError.message, {
                extra: {
                  locations: payloadError.locations,
                  severity: payloadError.severity,
                },
              });
              toast.error(
                `An unexpected error occurred. Our team has been notified. (${eventId})`,
                { role: 'alert' },
              );
            }

            return;
          }

          if (apiErrors) {
            for (const apiError of apiErrors) {
              toast.error(apiError.message);
            }
          }

          if (
            onSuccess &&
            !hasErrors &&
            response?.createUserList.userList?.id
          ) {
            onSuccess(response.createUserList.userList.id);
          }
        },
        onError: (error) => {
          const eventId = captureException(error);
          toast.error(
            `Oops! Something went wrong. Our team has been notified. (${eventId})`,
            { role: 'alert' },
          );
        },
        variables: {
          input: {
            displayName: name,
            userListGroupNid: 'NETWORK_LIST',
          },
        },
      });
    },
    [createList, toast, track],
  );

  const [updateList, isUpdatingList] = useContraMutation(graphql`
    mutation useUserListMutationsUpdateUserListMutation(
      $input: UpdateUserListInput!
    ) {
      updateUserList(input: $input) {
        errors {
          __typename
          message
        }
        userAccount {
          ...NetworkTableV2_userLists_userAccount
          ...AddToUserListsModal_userAccount
        }
      }
    }
  `);

  const updateUserList = useCallback(
    (
      {
        displayName,
        isPublic,
        userListId,
      }: Pick<UpdateUserListInput, 'displayName' | 'isPublic' | 'userListId'>,
      onSuccess?: () => void,
    ) => {
      updateList({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;

          if (payloadErrors) {
            for (const payloadError of payloadErrors) {
              const eventId = captureException(payloadError.message, {
                extra: {
                  locations: payloadError.locations,
                  severity: payloadError.severity,
                },
              });
              toast.error(
                `An unexpected error occurred. Our team has been notified. (${eventId})`,
                { role: 'alert' },
              );
            }

            return;
          }

          if (apiErrors) {
            for (const apiError of apiErrors) {
              toast.error(apiError.message);
            }
          }

          if (!hasErrors) {
            onSuccess?.();
          }
        },
        onError: (error) => {
          const eventId = captureException(error);
          toast.error(
            `Oops! Something went wrong. Our team has been notified. (${eventId})`,
            { role: 'alert' },
          );
        },
        variables: {
          input: {
            displayName,
            isPublic,
            userListId,
          },
        },
      });
    },
    [toast, updateList],
  );

  return {
    createUserList,
    isCreatingNewList,
    isUpdatingList,
    updateUserList,
  };
};
